'use client';

import { useRef } from 'react';
import Icon from '@/components/ui/AppIcon';


const proofCards = [
{
  icon: 'ClockIcon',
  label: 'Beschikbaarheid',
  value: '24u/24 · 7d/7',
  sub: 'Spoedinterventies',
  float: 'float-a',
  delay: '0.5s',
  offset: ''
},
{
  icon: 'ShieldCheckIcon',
  label: 'Transparantie',
  value: 'Schriftelijke offerte',
  sub: 'Prijs bevestigd vóór werken',
  float: 'float-b',
  delay: '0.7s',
  offset: 'md:-mt-10'
},
{
  icon: 'StarIcon',
  label: 'Garantie',
  value: '2 jaar',
  sub: 'Op alle interventies',
  float: 'float-c',
  delay: '0.9s',
  offset: ''
}];


export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col justify-center pt-28 pb-20 overflow-hidden"
      id="hero">
      
      {/* Background image with overlay */}
      {/* Mobile background (below md) */}
      <div
        className="absolute inset-0 z-0 block md:hidden"
        style={{
          backgroundImage: `url('/assets/images/Gemini_Generated_Image_bi1ne8bi1ne8bi1n-1772292458972_copy-1772890674339.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div
          className="absolute inset-0"
          style={{ background: '#0503334b' }}
        />
      </div>
      {/* Desktop/Tablet background (md and above) */}
      <div
        className="absolute inset-0 z-0 hidden md:block"
        style={{
          backgroundImage: `url('/assets/images/Gemini_Generated_Image_bi1ne8bi1ne8bi1n-1772292053689.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark overlay for readability */}
        <div
          className="absolute inset-0"
          style={{ background: '#0503334b' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 w-full">
        {/* Badge */}
        <div className="flex justify-center mb-8">
          <div
            className="glass-card inline-flex items-center gap-3 px-4 py-2 rounded-full animate-fade-in">
            
            <span
              className="w-2 h-2 rounded-full"
              style={{ background: '#22C55E', boxShadow: '0 0 10px rgba(34,197,94,0.6)' }} />
            
         <span
              className="font-semibold tracking-[0.15em] uppercase"
              style={{ fontSize: '10px', color: '#4A6580' }}>GECERTIFICEERD LOODGIETER · NU BESCHIKBAAR


            </span>
          </div>
        </div>

        {/* Headline */}
        <h1
          className="font-display text-center leading-[1.05] mb-6 animate-fade-in-delay"
          style={{
            fontSize: 'clamp(2.4rem, 7vw, 5.5rem)',
            fontWeight: 700,
            color: '#ffffffff'
          }}>
          
          Loodgieter{' '}
          <span
            style={{
              background: 'linear-gradient(135deg, #16A34A 0%, #4ADE80 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
            
            24u/24
          </span>
          <br />
          <span
            className="font-display italic text-white"
            style={{ fontSize: '0.6em', fontWeight: 400, color: '#ffffffff' }}>Binnen 30 min ter plaatse


          </span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-center max-w-2xl mx-auto text-lg font-light leading-relaxed mb-10 animate-fade-up-1 text-white"
          style={{ color: '#ffffffff' }}>
          
          Prijs aangekondigd <strong style={{ color: '#ffffffff' }}>vóór elke interventie</strong>. Gratis en schriftelijke offerte. 
          Snelle interventie in heel België.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-up-2">
          <a
            href="tel:+32496538283"
            className="btn-primary shine-sweep w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-base"
            style={{ minWidth: '200px' }}>
            
            <Icon name="PhoneIcon" size={20} className="text-white" />
            Nu bellen
          </a>
          <a
            href="https://wa.link/kusumk"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary w-full sm:w-auto flex items-center justify-center gap-3 px-8 py-4 rounded-full font-bold text-base glass-card"
            style={{ minWidth: '200px' }}>
            
            <Icon name="ChatBubbleLeftRightIcon" size={20} style={{ color: '#16A34A' }} />
            WhatsApp
          </a>
        </div>

        {/* Proof stats */}
        <div
          className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mb-16 animate-fade-up-3">
          
          {[
          { icon: 'TrophyIcon', label: '10+ jaar ervaring' },
          { icon: 'UsersIcon', label: 'Honderden tevreden klanten' },
          { icon: 'BoltIcon', label: 'Interventie binnen 30min ter plaatsen' }].
          map((stat) =>
          <div key={stat.label} className="flex items-center gap-2">
              <Icon name={stat.icon as any} size={16} style={{ color: '#16A34A' }} />
              <span className="text-sm font-medium text-[rgba(252,252,252,1)]" style={{ color: '#ffffffff' }}>
                {stat.label}
              </span>
            </div>
          )}
        </div>

        {/* Floating proof cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-3xl mx-auto animate-fade-up-4">
          {proofCards.map((card) =>
          <div
            key={card.label}
            className={`glass-card rounded-2xl p-5 ${card.float} ${card.offset} group hover:border-primary-DEFAULT transition-all duration-500`}
            style={{ borderColor: 'rgba(22,163,74,0.15)' }}>
            
              {/* Icon line */}
              <div
              className="w-8 h-8 rounded-xl flex items-center justify-center mb-3"
              style={{ background: 'rgba(232,96,10,0.1)' }}>
              
                <Icon name={card.icon as any} size={18} style={{ color: '#16A34A' }} />
              </div>
              {/* Line reveal */}
              <div
              className="service-line h-[1px] mb-3 group-hover:scale-x-100"
              style={{
                background: 'linear-gradient(90deg, #16A34A, transparent)',
                transformOrigin: 'left',
                transform: 'scaleX(0)',
                transition: 'transform 0.4s cubic-bezier(0.165,0.84,0.44,1)'
              }} />
            
              <p className="text-xs font-semibold uppercase tracking-[0.15em] mb-1" style={{ color: '#16A34A' }}>
                {card.label}
              </p>
              <p className="font-display text-xl font-semibold mb-1" style={{ color: '#1A2B3C' }}>{card.value}</p>
              <p className="text-xs" style={{ color: '#7A95AD' }}>{card.sub}</p>
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs tracking-[0.2em] uppercase" style={{ color: '#7A95AD' }}>Scrollen</span>
        <Icon name="ChevronDownIcon" size={16} style={{ color: '#7A95AD' }} />
      </div>
    </section>);

}
